
import { supabase } from '@/integrations/supabase/client';
import { LessonResource } from '@/types/supabase';

/**
 * Fetch resources for a lesson
 */
export const fetchLessonResources = async (lessonId: string) => {
  try {
    const { data, error } = await supabase
      .from('lesson_resources')
      .select('*')
      .eq('lesson_id', lessonId)
      .order('created_at', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching lesson resources:', error);
    throw error;
  }
};

/**
 * Alias for fetchLessonResources for backward compatibility
 */
export const fetchResourcesForLesson = fetchLessonResources;

/**
 * Create a new lesson resource
 */
export const createLessonResource = async (resource: { title: string; url: string; type: string; lesson_id: string; is_required?: boolean }): Promise<LessonResource | null> => {
  const { data, error } = await supabase
    .from('lesson_resources')
    .insert(resource)
    .select()
    .single();

  if (error) {
    console.error('Error creating lesson resource:', error);
    return null;
  }

  return {
    ...data,
    type: data.type as LessonResource['type']
  } as LessonResource;
};

/**
 * Update an existing lesson resource
 */
export const updateLessonResource = async (resourceId: string, updates: Partial<LessonResource>): Promise<LessonResource | null> => {
  const { data, error } = await supabase
    .from('lesson_resources')
    .update(updates)
    .eq('id', resourceId)
    .select()
    .single();

  if (error) {
    console.error('Error updating lesson resource:', error);
    return null;
  }

  return {
    ...data,
    type: data.type as LessonResource['type']
  } as LessonResource;
};

/**
 * Delete a lesson resource
 */
export const deleteLessonResource = async (resourceId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('lesson_resources')
    .delete()
    .eq('id', resourceId);

  if (error) {
    console.error('Error deleting lesson resource:', error);
    return false;
  }

  return true;
};
